import * as Sentry from '@sentry/react';

import type { ChartData } from '@/types/chart';

/**
 * Normalize a value from a given range to 0-1
 */
export function normalizeValue(value: number, min: number, max: number): number {
  if (max === min) return 0;
  return (value - min) / (max - min);
}

/**
 * Apply logarithmic scale to an already-normalized 0-1 value
 * This compresses high values and expands low values for better visualization
 */
export function applyLogScale(normalizedValue: number): number {
  if (normalizedValue <= 0) return 0;
  if (normalizedValue >= 1) return 1;

  // Map 0-1 through log space
  // We use a small epsilon to avoid log(0)
  const epsilon = 0.001;
  const shifted = normalizedValue * (1 - epsilon) + epsilon;

  const logValue = Math.log10(shifted);
  const logMin = Math.log10(epsilon);
  const logMax = Math.log10(1);

  return (logValue - logMin) / (logMax - logMin);
}

/**
 * Get the normalized value for display, considering the chart's value range,
 * category mappings, and optional log scale transformation
 */
export function getNormalizedValue(
  chartData: ChartData,
  rawValue: number,
  useLogScale = false,
  label?: string,
): number {
  // Step 1: Normalize to 0-1
  let normalized: number;

  // Check if we have a category mapping
  if (chartData.categoryMapping) {
    if (label === undefined) {
      // Undefined/null label means this value is not in any category
      normalized = 0;
    } else {
      const { categories, direction = 'ascending' } = chartData.categoryMapping;
      const index = categories.indexOf(label);

      if (index !== -1) {
        // Map category to evenly distributed 0-1 range
        // For ascending: first category (index 0) = low value, last = high value
        // For descending: first category (index 0) = high value, last = low value
        const effectiveIndex = direction === 'descending' ? categories.length - 1 - index : index;
        normalized = (effectiveIndex + 1) / categories.length;
      } else {
        // Label not found in categories, fall back to raw value
        Sentry.logger.warn(`Category label "${label}" not found in mapping, using raw value`);
        normalized = rawValue;
      }
    }
  } else if (!chartData.valueRange) {
    // Already normalized to 0-1
    normalized = rawValue;
  } else {
    const { min, max } = chartData.valueRange;
    normalized = normalizeValue(rawValue, min, max);
  }

  // Step 2: Optionally apply log scale to the normalized value
  if (useLogScale) {
    normalized = applyLogScale(normalized);
  }

  return normalized;
}

/**
 * Auto-detect and set the value range for a chart if not specified
 */
export function autoDetectValueRange(chartData: ChartData): ChartData {
  if (chartData.valueRange) {
    return chartData; // Already has a range
  }

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  // Iterate over grid cells
  for (const row of chartData.hands) {
    for (const cell of row) {
      if (cell === null) continue;

      if (typeof cell === 'number') {
        min = Math.min(min, cell);
        max = Math.max(max, cell);
      } else if (Array.isArray(cell)) {
        // Multi-segment cell
        for (const seg of cell) {
          const value = typeof seg === 'number' ? seg : seg.value;
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
      }
      // Skip string cells (categorical)
    }
  }

  // If values are already in 0-1 range (or close to it), don't add a range
  if (min >= 0 && max <= 1.0001) {
    return chartData;
  }

  return {
    ...chartData,
    valueRange: { min, max },
  };
}
